/**
 * Ход прогона — полоса в шапке слева от кнопки «Запустить генерацию».
 *
 * ЗАЧЕМ ОН ВООБЩЕ ЕСТЬ. Кнопка кладёт заявку в очередь, сервис генерации забирает её
 * ближайшим опросом (демон, ~1500 мс — `SMM_WRITER_POLL_MS`), и прогон идёт десятки секунд:
 * модель пишет, гейт живого текста читает написанное, разметка канонизируется. Без полосы
 * всё это время интерфейс молчит, и человеку остаётся один вывод — «не сработало»; он жмёт
 * кнопку второй раз и получает второй пост. Полоса отвечает на «идёт ли что-нибудь» тем же
 * взглядом, которым человек искал кнопку.
 *
 * ДВЕ ОЧЕРЕДИ, ОДНА ПОЛОСА (правка 11, §9.2 `docs/CHANGES-2026-09-08.md`). Переделка — такой
 * же прогон писателя, но строки заявки у неё нет: владельцем прогресса стал `rework_id`
 * (миграция 0017), а очередь читается своим человеческим маршрутом
 * `GET /api/v1/rework-requests`. Полоса опрашивает ОБА маршрута, сливает ответы в один
 * список кандидатов и выбирает из него одну запись. До этой правки человек, нажавший «на
 * переделку», не видел в шапке НИЧЕГО — и диалоги честно писали, что полосы у переделки нет.
 *
 * РАЗЛИЧИТЕЛЬ ВИДА СТАВИТ ИНТЕРФЕЙС, А НЕ ПРОВОД. `RunKind` в теле ответа отсутствует
 * намеренно (см. шапку `api/generation.ts`): поле, всегда равное маршруту, откуда тело
 * приехало, — это лишнее поле в схеме, которую читают три службы. Здесь оно ставится в одном
 * месте — `runOfRequest` / `runOfRework`, — и дальше весь код работает с общей формой `Run`.
 *
 * ПОЧЕМУ ОПРОС, А НЕ ПОДПИСКА. Событийного канала (SSE/WebSocket) у бэкенда нет, и заводить
 * его ради одной полосы — это новый долгоживущий коннект на каждую вкладку и новый повод
 * для 05-ui.md §1.3. Опрос здесь дешёвый и, главное, ПЕРЕМЕННЫЙ: пока прогон идёт — раз в
 * ACTIVE_POLL_MS, как только активных нет — раз в IDLE_POLL_MS. Редкий фоновый опрос
 * не выключен совсем намеренно: заявку мог оставить второй человек в другой вкладке, и
 * шапка обязана это показать, а не ждать перезагрузки страницы.
 *
 * ОЧЕРЕДЬ ПЕРЕДЕЛОК ДЕГРАДИРУЕТ МОЛЧА. Её маршрут появился этой же партией правок, и на
 * бэкенде без неё (старый прод, откат) он отвечает 404. Тогда список переделок остаётся
 * пустым, а полоса продолжает показывать генерации — то есть ровно то, что она показывала
 * до правки. Отказ одного из двух запросов не гасит второй и не рисует плашку.
 *
 * ПОЧЕМУ НЕ `useResource`. Тот делает ровно один запрос на путь и обновляется по явному
 * refetch — это правильно для экрана и неправильно для поллера: здесь нужен переменный
 * интервал, пересчёт задержки по ПОСЛЕДНЕМУ ответу и два пути в одном тике. Гонка ответов
 * гасится тем же AbortController, что и там, а размонтирование снимает и запрос, и таймер.
 *
 * ПОЧЕМУ ПОЛОСА НЕ ЕДЕТ НАЗАД. Заполнение считается по САМОМУ ДАЛЬНЕМУ этапу из присланных
 * шагов, а не по последнему: повтор этапа после ретрая — законное поведение сервиса, а
 * полоса, отскочившая назад, читается как «сломалось». По той же причине это чистая функция
 * от данных, а не накопленное в состоянии число: перерисовка тем же ответом не двигает её
 * вовсе.
 *
 * ЧЕГО ЗДЕСЬ НЕТ. Пустого органа: пока активного прогона нет и рассказывать не о чем,
 * компонент возвращает null, и шапка выглядит так же, как выглядела. Исход (готовый
 * черновик, отказ, отказ переделки) висит до тех пор, пока человек его не закроет, — прогон,
 * о котором никто не узнал, ничем не лучше молчания.
 */

import { nowMs } from '@smm/contracts';
import {
  GENERATION_QUEUE_MAX_PER_BRAND,
  GENERATION_STAGES,
  type WorkItemView,
  type WorkTerminalReason,
} from '@smm/contracts/schemas';
import { type JSX, useEffect, useRef, useState } from 'react';
import { Link } from 'wouter';
import { get } from '../api/client.ts';
import type {
  GenerationProgressStep,
  GenerationRequest,
  GenerationRequestsResponse,
  GenerationStage,
  ReworkRun,
  ReworkRunsResponse,
  ReworkTarget,
  RunKind,
} from '../api/types.ts';
import { momentMsk, plural } from './format.ts';
import { IconX } from './icons.tsx';
import styles from './RunStatusBar.module.css';

/** Пока прогон идёт: секунда с небольшим — шаг заметен глазом, а не «когда-нибудь». */
const ACTIVE_POLL_MS = 1200;

/**
 * Когда активных прогонов нет. Это не «на всякий случай», а единственный способ узнать про
 * заявку, оставленную не в этой вкладке; для собственного нажатия есть `accepted`.
 */
const IDLE_POLL_MS = 15_000;

/**
 * Окно списка, пока работы нет: шести записей хватает — новая заявка всегда свежайшая, и
 * опрос раз в 15 с заметит её первой же записью. Очередь переделок читается этим окном
 * всегда: переделки не заказывают пачкой.
 */
const LIST_LIMIT = 6;

/**
 * Окно списка ЗАЯВОК, пока работа идёт (правка 19.09, docs/release-2026-09-19.md §3.4).
 * Таблица прогона мониторинга ставит до десяти заявок одним нажатием, и шести свежих
 * перестало хватать: очередь разбирается с головы, поэтому пишется САМАЯ СТАРАЯ из пачки —
 * ровно та, что в окно из шести свежих не попадает. Полоса показала бы «в очереди» над
 * заявкой, которую никто не пишет, а «+N» насчитал бы меньше, чем стоит в очереди. Очередь
 * бренда ограничена бэкендом (`GENERATION_QUEUE_MAX_PER_BRAND` из контрактов), взятых —
 * единицы: потолок очереди плюс четыре покрывают всех живых с запасом.
 * Широкое окно — только пока частый опрос: в простое шапка по-прежнему везёт шесть записей.
 */
const ACTIVE_LIST_LIMIT = GENERATION_QUEUE_MAX_PER_BRAND + 4;

/** Словарь этапов — из контракта, по-русски. Ключи закрыты схемой, поэтому Record без запаса. */
const STAGE_LABEL: Readonly<Record<GenerationStage, string>> = {
  claimed: 'заявка принята',
  topic: 'тема',
  format: 'формат',
  style_roll: 'бросок формы',
  write: 'пишем',
  lint: 'гейт живого текста',
  canonicalize: 'канонизация',
  // Стадия стоит между канонизацией и отправкой ровно там, где она идёт в пайплайне:
  // вердикт «рисовать / не рисовать» и рендер карточки выносятся ПОСЛЕ выбранной версии.
  // Без этой строки полоса замирала бы на «канонизации» те секунды, что идёт рендер.
  visual: 'картинка',
  submit: 'отправка',
  done: 'готово',
  failed: 'не вышло',
};

/**
 * Этапы работы — те, по которым меряется заполнение. `done`/`failed` из шкалы исключены:
 * это исход прогона, а не его часть, и делить полосу на одиннадцать там, где работы девять,
 * значит показывать 90% на готовом посте. Порядок берётся из контракта, а не задаётся
 * здесь: второй список этапов разошёлся бы с первым молча. Переделка идёт ПОДМНОЖЕСТВОМ
 * этих же этапов (словарь стадий правкой 11 не расширялся), поэтому шкала у неё общая.
 */
const WORK_STAGES: readonly GenerationStage[] = GENERATION_STAGES.filter(
  (s) => s !== 'done' && s !== 'failed',
);

/**
 * ОБЩАЯ ФОРМА ПРОГОНА. Заявка и переделка описаны разными схемами и по делу: у заявки есть
 * `state: taken`, у переделки — `targets` и номер закрывшей версии. Полосе же нужно одно и
 * то же от обеих, и склеивать их условиями по месту («если это переделка, то поле называется
 * иначе») значит рассыпать различитель по всему файлу. Приведение — в двух функциях ниже,
 * дальше по файлу вид читается только там, где он МЕНЯЕТ СМЫСЛ: в подписи и в ярлыках.
 */
interface Run {
  readonly kind: RunKind;
  /** Ключ слежения. С префиксом вида: идентификатор уникален в своей очереди, не между ними. */
  readonly key: string;
  readonly brand: string;
  /** Момент нажатия человеком. По нему решается спор двух очередей — см. `pickActive`. */
  readonly createdAt: string;
  /** Сервис уже внутри этой работы. Есть только у заявок: переделку никто не «берёт». */
  readonly taken: boolean;
  /** Работа ещё идёт: полосу показываем, опрос держим частым. */
  readonly running: boolean;
  /** Терминальный исход или `null`, пока работа идёт. */
  readonly outcome: 'done' | 'failed' | 'cancelled' | null;
  /** Куда вести человека по исходу. У переделки известен всегда, у заявки — только по `done`. */
  readonly draftId: string | null;
  /** Причина отказа словами сервиса. */
  readonly outcomeDetail: string | null;
  /** Что именно переделывается. Пусто у заявки. */
  readonly targets: readonly ReworkTarget[];
  /** Версия, которой закрылась переделка. */
  readonly closedVersion: number | null;
  readonly progress: readonly GenerationProgressStep[];
  /**
   * Повтор после временного отказа (P3): сколько попыток уже было и когда следующая. Работа
   * при этом «идёт» — она в очереди повтора, а не закрыта. `null` — повтора нет.
   */
  readonly retry: { readonly attempt: number; readonly nextAt: string } | null;
  /** Почему работа снята (P3, `work.terminal_reason`); `null` — не снята либо причины нет. */
  readonly removed: WorkTerminalReason | null;
}

/**
 * Причина снятия словами — для «снята: причина». Словарь закрыт контрактом
 * (`WORK_TERMINAL_REASONS`), и Record без запаса ловит новую причину компилятором.
 */
const REMOVED_LABEL: Readonly<Record<WorkTerminalReason, string>> = {
  attempts_exhausted: 'попытки исчерпаны',
  deterministic_failure: 'повтор дал бы то же самое',
  model_refusal: 'модель отказалась',
  budget_stop: 'стоп по бюджету или ключу',
  cancelled_by_human: 'отменено',
  lineage_closed: 'заявка закрыта',
  draft_discarded: 'черновик удалён',
  expired: 'срок вышел',
  abandoned: 'не дождались мониторинга',
  stale: 'сервис генерации пропал',
};

/** Повтор после временного отказа: работа в очереди, попытки были, следующая назначена. */
function retryOf(work: WorkItemView | null | undefined): Run['retry'] {
  if (work === null || work === undefined) return null;
  if (work.state !== 'queued' || work.attempt < 1 || work.next_attempt_at === null) return null;
  return { attempt: work.attempt, nextAt: work.next_attempt_at };
}

function removedOf(work: WorkItemView | null | undefined): WorkTerminalReason | null {
  if (work === null || work === undefined) return null;
  return work.state === 'failed' || work.state === 'cancelled' ? work.terminal_reason : null;
}

/**
 * Шаги прогона. Проверка формы не лишняя: `progress` появился в контракте вместе с этим
 * баром, и бэкенд, который ещё не научился его отдавать, обязан давать молчащую полосу, а
 * не белый экран.
 */
const stepsOf = (progress: unknown): readonly GenerationProgressStep[] =>
  Array.isArray(progress) ? (progress as readonly GenerationProgressStep[]) : [];

const runOfRequest = (r: GenerationRequest): Run => ({
  kind: 'generation',
  key: `generation:${r.request_id}`,
  brand: r.brand,
  createdAt: r.created_at,
  taken: r.state === 'taken',
  running: r.state === 'queued' || r.state === 'taken',
  outcome: r.state === 'done' || r.state === 'failed' || r.state === 'cancelled' ? r.state : null,
  draftId: r.draft_id,
  outcomeDetail: r.outcome_detail,
  targets: [],
  closedVersion: null,
  progress: stepsOf(r.progress),
  retry: r.state === 'queued' ? retryOf(r.work) : null,
  removed: r.state === 'failed' ? removedOf(r.work) : null,
});

/**
 * ТЕРМИНАЛЬНЫЙ ОТКАЗ ПЕРЕДЕЛКИ ВИДЕН ТОЛЬКО ИЗ ШАГОВ, и это не пропуск контракта. Переделка
 * визуала может кончиться вообще без новой версии («перерисовывать нечего»), а строка
 * `rework_request` закрывается ТОЛЬКО версией (`CHECK (status='closed') =
 * (closed_by_version_id IS NOT NULL)`) — колонки исхода у неё нет. Поэтому отказ приезжает
 * последним шагом `{stage:'failed'}`, а строка остаётся ОТКРЫТОЙ. Считать такую строку
 * «идущей» нельзя: полоса вечно дышала бы точкой над мёртвым прогоном и вечно держала бы
 * частый опрос.
 */
function runOfRework(r: ReworkRun): Run {
  const progress = stepsOf(r.progress);
  const refusal = progress.findLast((s) => s.stage === 'failed') ?? null;
  const closed = r.state === 'closed';
  // С P3 судьбу переделки знает её запись работы: снята отказом (`failed`), отменена удалением
  // черновика (`cancelled` — решение человека, полоса его не показывает), ждёт повтора. Без
  // записи (бэкенд старше P3) — прежний признак: шаг `failed` у открытой строки.
  const work = r.work ?? null;
  const cancelled = !closed && work?.state === 'cancelled';
  const failed =
    !closed && !cancelled && (work === null ? refusal !== null : work.state === 'failed');
  return {
    kind: 'rework',
    key: `rework:${r.rework_id}`,
    brand: r.brand,
    createdAt: r.created_at,
    taken: false,
    running: !closed && !cancelled && !failed,
    outcome: closed ? 'done' : cancelled ? 'cancelled' : failed ? 'failed' : null,
    draftId: r.draft_id,
    outcomeDetail: refusal?.detail ?? work?.last_failure_detail ?? null,
    targets: Array.isArray(r.targets) ? r.targets : [],
    closedVersion: r.closed_by_version_no,
    progress,
    retry: closed ? null : retryOf(work),
    removed: failed ? removedOf(work) : null,
  };
}

/**
 * Прогон, который сервис ВЕДЁТ ПРЯМО СЕЙЧАС, — тот единственный, о ходе которого есть что
 * рассказать. Правило двухуровневое, и оба уровня объявлены, потому что оставить их порядку
 * сортировки значило бы сделать выбор случайным на вид.
 *
 * ВНУТРИ ОЧЕРЕДИ ЗАЯВОК порядок повторяет порядок работы сервиса, а не порядок нажатий:
 * сначала взятая (`taken` — сервис уже в ней), при равенстве — самая СТАРАЯ. Очередь
 * разбирается с головы (`generationTasks`: `ORDER BY created_at_ms, id`, по одной), поэтому
 * «самая свежая» — это ровно та заявка, которая ещё не начиналась: полоса замерла бы на 4%
 * на всё время работы над предыдущей и не показала бы ни одного её шага. Очередь переделок
 * разбирается так же с головы, поэтому и там побеждает старшая.
 *
 * МЕЖДУ ОЧЕРЕДЯМИ побеждает то, чего ЧЕЛОВЕК КОСНУЛСЯ ПОЗЖЕ (больший `created_at`). Это
 * решение, а не следствие сортировки: обе записи создаёт нажатие человека, и полоса
 * отвечает на «что я только что запустил». Обратное правило («старшее вперёд») показывало бы
 * заявку получасовой давности человеку, который секунду назад отправил пост на переделку.
 */
function pickActive(runs: readonly Run[]): Run | null {
  const bestOf = (kind: RunKind): Run | null => {
    let best: Run | null = null;
    for (const r of runs) {
      if (r.kind !== kind || !r.running) continue;
      if (best === null) {
        best = r;
        continue;
      }
      if (best.taken && !r.taken) continue;
      // `created_at` — ISO_UTC_MS фиксированной ширины: сравнение строк здесь и есть
      // сравнение моментов, и оно не требует разбора времени (правило R11).
      if (r.taken && !best.taken) best = r;
      else if (r.createdAt < best.createdAt) best = r;
    }
    return best;
  };
  const generation = bestOf('generation');
  const rework = bestOf('rework');
  if (generation === null) return rework;
  if (rework === null) return generation;
  return rework.createdAt >= generation.createdAt ? rework : generation;
}

/** Что переделывается — словами, для подписи до первого шага. */
function targetsWord(targets: readonly ReworkTarget[]): string {
  const text = targets.includes('text');
  const visual = targets.includes('visual');
  if (text && visual) return 'текст и картинка';
  if (visual) return 'картинка';
  return 'текст';
}

/**
 * ТИШИНА ПОСЛЕ ОБОРВАННОГО ПРОГОНА (§9.1). Пятнадцать минут после сорвавшегося прогона демон
 * переделки не подхватывает — защита от того, чтобы упереться в ту же квоту и сжечь вторую
 * платную серию. Защита остаётся, но перестаёт быть немой: писатель шлёт ждущей переделке
 * один шаг `claimed/started` с оставшимся временем (`daemon.ts::nameTheSilence`).
 *
 * Различитель — пара (стадия, состояние), а не текст детали: на рабочем пути переделка
 * получает `claimed` только со `state: 'ok'` (`rework.ts:553`), поэтому `claimed/started`
 * встречается ровно в одном месте кода. Сверять детали строкой было бы хрупко — фраза живёт
 * у писателя и меняется там.
 */
const isWaitingOut = (run: Run): boolean => {
  const last = run.progress.at(-1);
  return run.kind === 'rework' && last?.stage === 'claimed' && last.state === 'started';
};

/** Доля пройденного, 0..1. Половина этапа — «начали», целый — «закончили». */
function fillOf(run: Run): number {
  if (run.outcome === 'done') return 1;
  let best = 0;
  for (const step of run.progress) {
    const i = WORK_STAGES.indexOf(step.stage);
    if (i < 0) continue;
    const v = (i + (step.state === 'ok' ? 1 : 0.5)) / WORK_STAGES.length;
    if (v > best) best = v;
  }
  // Прогон в очереди — уже видимая полоска: пустая дорожка читается как «ничего не идёт».
  return Math.max(best, 0.04);
}

/**
 * Чем подписан прогон: этапом, на котором он стоит, либо исходом.
 *
 * У переделки подпись начинается со слова «переделка» ВСЕГДА: две работы над одним каналом
 * различаются только здесь, и «пишем» без уточнения читается как новый пост. Этап при этом
 * тот же самый, поэтому переписывание текста подписано «переделка: пишем», а перерисовка
 * карточки — «переделка: картинка»: стадии `write` и `visual` совпадают с тем, что человек
 * отметил в `targets`. До первого шага стадии ещё нет — тогда и говорят `targets`.
 */
function headline(run: Run): string {
  if (run.retry !== null) return run.kind === 'rework' ? 'переделка: повтор' : 'повтор';
  if (run.kind === 'generation') {
    if (run.outcome === 'done') return 'черновик готов';
    if (run.outcome === 'failed') return STAGE_LABEL.failed;
    const last = run.progress.at(-1);
    if (last === undefined) return run.taken ? STAGE_LABEL.claimed : 'в очереди';
    return STAGE_LABEL[last.stage];
  }
  if (run.outcome === 'done') return 'переделка готова';
  if (run.outcome === 'failed') return 'переделка не вышла';
  if (isWaitingOut(run)) return 'переделка ждёт';
  const last = run.progress.at(-1);
  if (last === undefined) return `переделка в очереди: ${targetsWord(run.targets)}`;
  return `переделка: ${STAGE_LABEL[last.stage]}`;
}

/**
 * Мелкая строка под подписью. У отказа это причина: у заявки — `outcome_detail`, то, что
 * сервис написал, закрывая её; у переделки — деталь шага `failed`, потому что колонки исхода
 * у переделки нет вовсе. Пустой причины не бывает: «не вышло» без единого слова не даёт
 * человеку сделать ничего.
 */
function detailOf(run: Run, now: number): string | null {
  const step = run.progress.at(-1)?.detail ?? null;
  if (run.outcome === 'done' && run.closedVersion !== null) {
    return `версия v${String(run.closedVersion)}`;
  }
  // P3: одна форма на заявку и переделку — «попытка N, следующая в HH:MM» и «снята: причина».
  if (run.retry !== null) {
    return `попытка ${String(run.retry.attempt)}, следующая в ${momentMsk(run.retry.nextAt, now)}`;
  }
  if (run.outcome !== 'failed') return step;
  const outcome = run.outcomeDetail === null ? '' : run.outcomeDetail.trim();
  if (run.removed !== null) {
    const why = `снята: ${REMOVED_LABEL[run.removed]}`;
    return outcome === '' ? why : `${why} — ${outcome}`;
  }
  if (outcome !== '') return outcome;
  return step ?? 'сервис не довёл прогон до конца';
}

export interface RunStatusBarProps {
  readonly brand: string;
  /**
   * Заявка, которую сервер только что принял в этой вкладке (ответ POST из диалога).
   *
   * Она нужна ради двух секунд между нажатием и первым ответом очереди: без неё человек
   * закрывает диалог и видит в шапке пустоту — то самое молчание, ради которого бар и
   * заведён. Показываем её сразу и опрашиваем очередь сразу; как только она приедет
   * ответом списка, эта копия перестаёт участвовать вовсе.
   *
   * У переделки такой копии нет и не нужно: её запускает диалог на экране черновика, а не
   * шапка, и первый же тик опроса (не позже 1200 мс) приносит открытую строку.
   */
  readonly accepted: GenerationRequest | null;
  /**
   * «Копия больше не нужна»: заявка приехала ответом очереди, и дальше о ней рассказывает
   * список. Без этого сигнала снимок из POST жил бы до перезагрузки страницы и через шесть
   * заявок (`LIST_LIMIT`) выпал бы из окна списка — а вместе с ним застряли бы и «в очереди»
   * в шапке, и частый опрос: список его больше не видит, значит «ещё не доехала» навсегда.
   */
  readonly onAcceptedSeen?: (() => void) | undefined;
}

export function RunStatusBar({
  brand,
  accepted,
  onAcceptedSeen,
}: RunStatusBarProps): JSX.Element | null {
  const [requests, setRequests] = useState<readonly GenerationRequest[]>([]);
  const [reworks, setReworks] = useState<readonly ReworkRun[]>([]);
  /** Прогон, за которым следим: только его исход мы имеем право показать. */
  const [watched, setWatched] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<string | null>(null);
  /** Читается в момент планирования следующего опроса — то есть всегда по последнему ответу. */
  const fast = useRef(false);
  // Через ref, а не в зависимостях эффекта: обработчик приходит новой функцией на каждую
  // отрисовку шапки, и в зависимостях он перезапускал бы опрос на ровном месте.
  const seen = useRef(onAcceptedSeen);
  useEffect(() => {
    seen.current = onAcceptedSeen;
  });

  // Снимок из POST принадлежит бренду, в котором нажимали кнопку. Переход на другой бренд —
  // это другая шапка и другая очередь: чужая заявка здесь означала бы «в очереди» под
  // каналом, где никто ничего не запускал.
  const mine = accepted !== null && accepted.brand === brand ? accepted : null;

  const brandQuery = `brand=${encodeURIComponent(brand)}`;
  const reworkPath = `/api/v1/rework-requests?${brandQuery}&limit=${String(LIST_LIMIT)}`;

  useEffect(() => {
    // Принятая заявка означает, что работа уже идёт: частый опрос начинается с первого тика,
    // а не после того, как ответ очереди это подтвердит.
    if (mine !== null) fast.current = true;

    const ac = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let stopped = false;

    const tick = (): void => {
      // Окно заявок — по темпу опроса, то есть по ПОСЛЕДНЕМУ ответу: работа идёт — широкое
      // (`ACTIVE_LIST_LIMIT`), нет — шесть свежих.
      const genPath = `/api/v1/generation-requests?${brandQuery}&limit=${String(
        fast.current ? ACTIVE_LIST_LIMIT : LIST_LIMIT,
      )}`;
      // Оба маршрута в одном тике и независимо друг от друга: отказ одного не имеет права
      // погасить второй. `null` означает «в этот раз не ответил» — прежний список остаётся,
      // и полоса не мигает пустотой из-за одного неудачного GET.
      Promise.all([
        get<GenerationRequestsResponse>(genPath, ac.signal).catch(() => null),
        get<ReworkRunsResponse>(reworkPath, ac.signal).catch(() => null),
      ])
        .then(([gen, rew]) => {
          if (ac.signal.aborted) return;
          // Ответы здесь ПРИВЕДЕНЫ типом, а не разобраны схемой, — как и в диалоге заявки.
          // Значит форму надо проверить руками: шапка живёт на каждом экране, и ответ,
          // который её не касается (старый бэкенд, чужой прокси, заглушка), не имеет права
          // уронить всё приложение из-за полосы под кнопкой.
          const freshRequests = gen !== null && Array.isArray(gen.requests) ? gen.requests : null;
          const freshReworks = rew !== null && Array.isArray(rew.reworks) ? rew.reworks : null;
          if (freshRequests !== null) setRequests(freshRequests);
          if (freshReworks !== null) setReworks(freshReworks);

          const runs = [
            ...(freshRequests ?? []).map(runOfRequest),
            ...(freshReworks ?? []).map(runOfRework),
          ];
          const act = pickActive(runs);
          // Пока принятая заявка не появилась в ответе списка, опрос остаётся частым:
          // «её ещё не видно» — это состояние гонки двух запросов, а не «работы нет».
          const arrived =
            mine !== null && (freshRequests ?? []).some((r) => r.request_id === mine.request_id);
          const awaited = mine !== null && !arrived;
          fast.current = act !== null || awaited;
          if (arrived) seen.current?.();
          // Следим только за тем, что при нас было живым: вчерашнее «готово» в шапке —
          // это новость, которой нет.
          if (act !== null) setWatched(act.key);
        })
        .catch(() => {
          // Разбор ответа сорвался — следующий опрос. Красная плашка в шапке из-за одного
          // неудачного тика научила бы её не замечать.
        })
        .finally(() => {
          if (stopped || ac.signal.aborted) return;
          timer = setTimeout(tick, fast.current ? ACTIVE_POLL_MS : IDLE_POLL_MS);
        });
    };

    tick();

    return () => {
      stopped = true;
      ac.abort();
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [brandQuery, reworkPath, mine]);

  // Пока очередь не вернула только что принятую заявку, она подставляется сюда сама: список
  // и POST — два запроса, и их порядок нам не подчиняется.
  const known: readonly Run[] = [
    ...(mine === null || requests.some((r) => r.request_id === mine.request_id)
      ? requests
      : [mine, ...requests]
    ).map(runOfRequest),
    ...reworks.map(runOfRework),
  ];

  const active = pickActive(known);
  const shownKey = active === null ? watched : active.key;
  const shown = shownKey === null ? undefined : known.find((r) => r.key === shownKey);

  if (shown === undefined) return null;
  // Отменённая заявка — не исход прогона, а решение человека: он его помнит.
  if (shown.outcome === 'cancelled') return null;

  const terminal = shown.outcome !== null;
  if (terminal && (shown.key !== watched || shown.key === dismissed)) return null;

  const rework = shown.kind === 'rework';
  const fill = Math.round(fillOf(shown) * 100);
  const label = headline(shown);
  const detail = detailOf(shown, nowMs());
  const failed = shown.outcome === 'failed';
  /**
   * Сколько ещё прогонов ждёт за показанным (правка 19.09). Полоса рассказывает об ОДНОМ —
   * о том, что сервис ведёт сейчас, — а заказ из таблицы прогона ставит до десяти заявок
   * сразу, и без счётчика очередь из девяти постов выглядела бы очередью из одного. Считаются
   * обе очереди: переделка — такая же работа писателя, и ждёт она его же. У исхода счётчика
   * нет по определению: исход показывается, только когда живых прогонов не осталось.
   */
  const waiting = terminal ? 0 : known.filter((r) => r.running && r.key !== shown.key).length;
  const waitingText =
    waiting === 0
      ? null
      : `ещё ${String(waiting)} ${plural(waiting, 'прогон', 'прогона', 'прогонов')} в очереди`;
  const title = [label, waitingText, detail].filter((s): s is string => s !== null).join(' · ');
  // У переделки ссылка на черновик уместна В ОБОИХ исходах: и по готовой версии, и по
  // отказу («перерисовывать нечего») человеку идти в один и тот же черновик — он остался
  // «на переделке», и вывести его оттуда может только человек.
  const draftHref =
    terminal && shown.draftId !== null && (shown.outcome === 'done' || rework)
      ? `/b/${encodeURIComponent(shown.brand)}/draft/${encodeURIComponent(shown.draftId)}`
      : null;

  return (
    // `title` несёт то же самое словами: на узком окне мелкая строка гаснет, а подпись
    // сжимается многоточием — наведение обязано возвращать полный текст.
    <div className={styles['wrap']} title={title}>
      <div className={styles['head']} role="status">
        {terminal ? null : <span className={styles['dot']} aria-hidden="true" />}
        {draftHref === null ? (
          <span className={failed ? `${styles['label']} ${styles['bad']}` : styles['label']}>
            {label}
          </span>
        ) : (
          <Link
            href={draftHref}
            className={
              failed
                ? `${styles['label']} ${styles['bad']}`
                : `${styles['label']} ${styles['link']}`
            }
          >
            {label}
          </Link>
        )}
        {waitingText === null ? null : (
          // Глазу — «+3», чтению с экрана — фраза: «плюс три» без существительного не
          // говорит, чего ещё три.
          <span className={styles['more']} data-testid="run-waiting">
            <span aria-hidden="true">+{waiting}</span>
            <span className={styles['srOnly']}>{waitingText}</span>
          </span>
        )}
        {detail === null ? null : (
          <span className={failed ? `${styles['detail']} ${styles['bad']}` : styles['detail']}>
            {detail}
          </span>
        )}
        {terminal ? (
          <button
            type="button"
            className={styles['hide']}
            aria-label={rework ? 'Скрыть ход переделки' : 'Скрыть ход генерации'}
            onClick={() => {
              setDismissed(shown.key);
            }}
          >
            <IconX width={12} height={12} />
          </button>
        ) : null}
      </div>
      <div
        className={styles['track']}
        role="progressbar"
        aria-label={rework ? 'Ход переделки' : 'Ход генерации'}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={fill}
      >
        <span
          className={failed ? `${styles['fill']} ${styles['stalled']}` : styles['fill']}
          style={{ width: `${String(fill)}%` }}
        />
      </div>
    </div>
  );
}
